/**
 * Sprint 4 US4 (T043 + T044) — SPA Legal Review section composer.
 *
 * Returns the 7 ReportSection entries that drive the SPA variant of
 * `ReportPage.tsx` per FR-SPA-038 + frontend/design/spa-report.html. The
 * page-level chrome (sticky TOC sidebar, topbar, download button, scroll
 * spy) stays shared with the DD render path. Only the section content
 * differs.
 *
 * The dispatch lives in ReportPage's `sections` useMemo:
 *   if (analysis.workflow_code === 'SPA') return buildSpaSections(...);
 *
 * Each section body uses the same CSS classes as the DD report (.data-table,
 * .badge, .source-tag, .clause-detected, .clause-missing) so no new
 * stylesheet work is needed — the design HTML at frontend/design/spa-
 * report.html was authored against the existing class set.
 */

import type { ReactNode } from 'react';
import { FormattedText, stripMarkdown, cleanTooltip } from '../FormattedText';
/* Sprint 5 client feedback 2026-07-07 (FE Ticket 1) — reuse the shared
   TruncatedCell for every text-heavy SPA report cell (Evidence, Issue
   description, Reviewer comments) so long LLM prose clamps to 2 lines
   with a "View" modal, matching the DD and Risk report behaviour. */
import { TruncatedCell } from '../TruncatedCell';
/* Sprint 5 client feedback 2026-07-11 (Iter6 #1) — every source
   citation renders via the canonical SourceChip so the SPA report
   matches DD + Risk exactly. */
import { SourceChip } from '../../utils/sourceChip';
import type {
  AnalysisResponse,
  AssessmentStatusResponse,
  SpaClauseRow,
  SpaIssueRow,
  SpaLegalFindingsSynth,
  SpaMandatorySectionRow,
  SpaSignoffSnapshot,
} from '../../types/api';

export interface SpaReportSection {
  id: string;
  num: string;
  title: string;
  body: ReactNode;
}

const STATUS_BADGES: Record<string, string> = {
  Approve: 'badge-pass',
  Conditional: 'badge-watch',
  Reject: 'badge-fail',
};

/* Sprint 5 client feedback 2026-07-12 (Iter9 #3) — 4-tier severity
 * vocab across the SPA report. Critical retired (folded to High for
 * legacy data); Recommendation added below Low (blue). Colours:
 *   High=badge-fail (red), Medium=badge-watch (amber),
 *   Low=badge-pass (green), Recommendation=badge-info (blue). */
const SEVERITY_BADGE: Record<string, string> = {
  Critical: 'badge-fail',       // legacy compat — fold to High display
  High: 'badge-fail',
  Medium: 'badge-watch',
  Low: 'badge-pass',
  Recommendation: 'badge-info',
};

/* Iter9 #3 — display fold. Any Critical-tagged row renders as
 * "High" in the SPA report per the new 4-tier vocab. Persisted
 * severity is not migrated (no BE change); this is a display-time
 * normalisation only. */
function _foldSevForDisplay(sev: string | null | undefined): string {
  const s = String(sev ?? '').trim();
  if (!s) return '—';
  if (s.toLowerCase() === 'critical') return 'High';
  return s;
}

const SUBTYPE_LABEL: Record<string, string> = {
  SPA: 'Share Purchase Agreement',
  SHA: 'Shareholders Agreement',
  Escrow: 'Escrow Agreement',
  Other: 'Supporting Document',
};

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return (
    d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' ' +
    d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  );
}

function clauseValueDisplay(row: SpaClauseRow): string {
  switch (row.data_type) {
    case 'boolean':
      if (row.value == null) return '—';
      return row.value ? 'Yes' : 'No';
    case 'numeric':
      if (row.numeric_value == null) return '—';
      return row.unit ? `${row.numeric_value}${row.unit === '%' ? '%' : ' ' + row.unit}` : String(row.numeric_value);
    case 'json_object': {
      const jv = row.json_value as Record<string, unknown> | null | undefined;
      if (!jv || typeof jv !== 'object') return '—';
      // Sprint 5 client feedback 2026-07-03 — §4.5 rows previously
      // rendered as "present: false, trigger_threshold_pct: —,
      // scope: —, excluded_transfers:" (raw dict dump). When the
      // clause is unambiguously absent (present: false), render
      // "Not detected" instead of the full dict. When present, show
      // a compact summary of populated fields only — skip null / — /
      // empty sub-fields that add noise without information.
      if (jv.present === false || (jv as { detected?: unknown }).detected === false) {
        return 'Not detected';
      }
      const populated = Object.entries(jv)
        .filter(([, v]) => v != null && v !== '' && v !== '—')
        .map(([k, v]) => `${k}: ${v}`);
      return populated.length > 0 ? populated.join(', ') : '—';
    }
    default:
      return row.raw_value ?? '—';
  }
}

/* Sprint 5 client feedback 2026-06-23 (FE Third Doc Issue 5) —
   clauseCategoryBadge was the legacy badge mapper for §04's old
   single-table view. It's been superseded by s04CategoryBadge below,
   which treats Adequate as info-blue per the design spec. The helper
   was removed (no other call sites) so we don't carry dead code. */


// ─────────────────────────────────────────────────────────────────────────────
// Section 1 — Executive Summary
// ─────────────────────────────────────────────────────────────────────────────

function ExecutiveSummary({
  analysis,
  sections,
  clauses,
  issues,
  signoff,
}: {
  analysis: AnalysisResponse;
  sections: SpaMandatorySectionRow[];
  clauses: SpaClauseRow[];
  issues: SpaIssueRow[];
  signoff: SpaSignoffSnapshot | null;
}): ReactNode {
  const status = analysis.overall_status ?? '—';
  const totalClauses = clauses.length;
  const sectionsPresent = sections.filter((s) => s.detected).length;
  const sectionsMissing = sections.length - sectionsPresent;
  /* Sprint 5 client feedback 2026-07-12 (Iter9 #3) — Critical
   * severity retired in the SPA report. All Critical rows fold into
   * High for the count + narrative; Recommendation is a new tier
   * below Low, blue-badged, populated by the two-step AI pipeline. */
  const criticalCount = issues.filter((i) => i.severity === 'Critical').length;
  const highOnlyCount = issues.filter((i) => i.severity === 'High').length;
  const highCount = criticalCount + highOnlyCount; // Critical folds into High
  const mediumCount = issues.filter((i) => i.severity === 'Medium').length;
  const lowCount = issues.filter((i) => i.severity === 'Low').length;
  const recommendationCount = issues.filter((i) => i.severity === 'Recommendation').length;
  const variant = status === 'Approve' ? 'is-pass' : status === 'Reject' ? 'is-fail' : 'is-watch';
  const dealName = analysis.assessment_id || 'this Share Purchase Agreement';

  /* Iter9 #3 — banner narrative no longer references "Critical".
   * Uses the folded High count so severity messaging aligns with
   * the new 4-tier vocabulary. */
  let summary: string;
  if (status === 'Approve') {
    summary = `${dealName} is approved — all benchmarks pass and no High-severity issues were detected by the automated review.`;
  } else if (status === 'Reject') {
    summary = `${dealName} is recommended for rejection — ${highCount} High-severity issue${highCount === 1 ? '' : 's'} require renegotiation before signing.`;
  } else {
    summary = `${dealName} is conditionally approved pending resolution of ${highCount} High-severity issue${highCount === 1 ? '' : 's'}. Review the issues table in Section 5 for required action items.`;
  }

  return (
    <>
      <div className={`result-card ${variant}`}>
        <div className="result-card-strip"></div>
        <div className="result-card-body">
          <span className="result-pill">{status} — Overall Legal Review Status</span>
          <p className="result-summary-lg">{summary}</p>
        </div>
      </div>
      {/* Sprint 5 client feedback 2026-07-07 (BE Issue #4) — unify the
          Executive Summary Value column so every row renders as a
          badge pill with a severity-appropriate colour. The prior
          layout mixed plain mono numerics (rows 1-3) with pills (rows
          4-7), producing ragged alignment and inconsistent visual
          weight. Now:
            * Every Value cell wraps a <span class="badge spa-exec-badge">
              pill. Shared min-width in index.css keeps pill edges
              aligned to the pixel grid.
            * Zero-value severity counters render badge-pass (green) —
              positive signal at a glance. Non-zero counters render
              with their severity colour.
            * Clauses Reviewed + Sign-off Status use badge-neutral
              since they're informational, not severity-scaled.
          The Metric labels, counter values, source column, and row
          order are unchanged. */}
      <div className="table-wrap">
        <table className="data-table spa-exec-summary" aria-label="Executive summary stats">
          <thead>
            <tr>
              <th>Metric</th>
              <th style={{ width: 120 }}>Value</th>
              <th>Source</th>
            </tr>
          </thead>
          <tbody>
            {/* Sprint 5 client feedback 2026-06-30 (R4) — the value
                here only counts §4 Investor Protection clauses, not
                the §3 mandatory sections. The prior label "Total
                Clauses Reviewed" was misread as covering everything.
                Rename to make the scope explicit. */}
            <tr><td className="td-fw5">Investor Protection Clauses Reviewed</td><td><span className="badge spa-exec-badge badge-neutral">{totalClauses}</span></td><td className="t-xs t-muted">Derived</td></tr>
            <tr><td className="td-fw5">Sections Present</td><td><span className="badge spa-exec-badge badge-pass">{sectionsPresent}</span></td><td className="t-xs t-muted">Derived</td></tr>
            <tr><td className="td-fw5">Sections Missing</td><td><span className={`badge spa-exec-badge ${sectionsMissing > 0 ? 'badge-fail' : 'badge-pass'}`}>{sectionsMissing}</span></td><td className="t-xs t-muted">Derived</td></tr>
            {/* Sprint 5 client feedback 2026-07-12 (Iter9 #3) —
                Critical Issues row retired. Legacy Critical rows
                fold into the High count above (highCount =
                criticalCount + highOnlyCount). Medium is now amber
                (not blue); Low added as green; Recommendation added
                as blue (new tier below Low). */}
            <tr><td className="td-fw5">High Issues</td><td><span className={`badge spa-exec-badge ${highCount > 0 ? 'badge-fail' : 'badge-pass'}`}>{highCount}</span></td><td className="t-xs t-muted">Derived</td></tr>
            <tr><td className="td-fw5">Medium Issues</td><td><span className={`badge spa-exec-badge ${mediumCount > 0 ? 'badge-watch' : 'badge-pass'}`}>{mediumCount}</span></td><td className="t-xs t-muted">Derived</td></tr>
            <tr><td className="td-fw5">Low Issues</td><td><span className={`badge spa-exec-badge ${lowCount > 0 ? 'badge-pass' : 'badge-neutral'}`}>{lowCount}</span></td><td className="t-xs t-muted">Derived</td></tr>
            <tr><td className="td-fw5">Recommendations</td><td><span className={`badge spa-exec-badge ${recommendationCount > 0 ? 'badge-info' : 'badge-neutral'}`}>{recommendationCount}</span></td><td className="t-xs t-muted">Derived</td></tr>
            <tr><td className="td-fw5">Sign-off Status</td><td>{signoff ? <span className={`badge spa-exec-badge ${STATUS_BADGES[signoff.decision] ?? 'badge-neutral'}`}>{signoff.decision}</span> : <span className="badge spa-exec-badge badge-neutral">Pending</span>}</td><td className="t-xs t-muted">Legal Sign-off</td></tr>
          </tbody>
        </table>
      </div>
    </>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Section 2 — Document Overview
// ─────────────────────────────────────────────────────────────────────────────

function DocumentOverview({ status }: { status: AssessmentStatusResponse }): ReactNode {
  const docs = status.documents ?? [];
  return (
    <div className="table-wrap">
      <table className="data-table" aria-label="Document overview">
        <thead>
          <tr>
            <th>Doc Type</th>
            <th>Filename</th>
            <th>Status</th>
            <th>Progress</th>
          </tr>
        </thead>
        <tbody>
          {docs.length === 0 ? (
            <tr><td colSpan={4} className="t-xs t-muted">No documents on record.</td></tr>
          ) : (
            docs.map((d, idx) => (
              <tr key={d.job_id ?? idx}>
                <td className="td-fw5">{SUBTYPE_LABEL[d.document_subtype ?? ''] ?? d.document_subtype ?? '—'}</td>
                <td>{d.filename}</td>
                <td>
                  {d.status === 'completed' || d.status === 'done' ? (
                    <span className="clause-detected">Processed</span>
                  ) : d.status === 'failed' ? (
                    <span className="clause-missing">Failed</span>
                  ) : (
                    <span className="badge badge-neutral">{d.status}</span>
                  )}
                </td>
                <td className="t-mono t-xs">{Math.round(d.progress_pct ?? 0)}%</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Section 3 — Mandatory Section Detection
// ─────────────────────────────────────────────────────────────────────────────

/* Sprint 5 client feedback 2026-07-11 (Iter3 SPA #6 + Iter4 S7
   correction) — the 6 core SPA.yaml mandatory sections are ALWAYS
   required for every SPA regardless of jurisdiction / investment
   type; they are Universal.

   Every other row in this table comes from the jurisdiction /
   investment_type detection layer (Closing Conditions,
   Confidentiality, Definitions, Dispute Resolution, Entire
   Agreement, Indemnification, Notices, Regulatory Approvals, and
   any future keys the detector adds). Those rows exist BECAUSE
   of the deal's specific jurisdiction + investment_type — the
   classification default therefore flips to Jurisdictional so
   new / unrecognised keys land on the correct side automatically.

   Iter4 correction: Governing Law was previously marked
   Jurisdictional here because "Governing Law" as a topic is
   inherently jurisdictional. But it IS one of the 6 core SPA.yaml
   keys required in every SPA (every agreement has to name its
   governing law), so it goes to Universal for classification
   purposes. The section's CONTENT is jurisdiction-specific; its
   PRESENCE is universal. */
const _MANDATORY_SECTION_UNIVERSAL_KEYS = new Set([
  'section_parties',
  'section_recitals',
  'section_purchase_price',
  'section_representations',
  'section_governing_law',
  'section_signatures',
]);

function _mandatorySectionClassification(attributeKey: string): 'Universal' | 'Jurisdictional' {
  return _MANDATORY_SECTION_UNIVERSAL_KEYS.has(attributeKey) ? 'Universal' : 'Jurisdictional';
}

/* Sprint 5 client feedback 2026-07-11 (Iter4 S6) — Classification
   header tooltip written in newline-formatted prose so the browser
   renders it as three readable paragraphs (title-style + Universal
   definition + Jurisdictional definition). Mentions both jurisdiction
   AND investment_type explicitly per the client's ask that the two
   dimensions we already track in the app show up in the definition. */
const _CLASSIFICATION_TOOLTIP =
  'Classification — how the section\'s applicability is scoped.\n\n'
  + 'Universal — the section is required in every SPA regardless of the deal\'s '
  + 'governing law or investment type. Any deal you review will need it.\n\n'
  + 'Jurisdictional — the section\'s specific requirements depend on the chosen '
  + 'governing law and/or the investment type recorded for the deal, so the '
  + 'expected content varies from one review to another.';

/* Sprint 5 client feedback 2026-07-11 (Iter3 SPA #10 + Iter4 D5/D7)
   — Status column header tooltip. Iter4 reformats it with line
   breaks so the browser renders it as a scannable two-line
   definition rather than one wrapped sentence. Present is the
   terminal positive; Missing is the terminal absence. */
const _MANDATORY_STATUS_TOOLTIP =
  'Status — final AI verdict on whether this mandatory section was located.\n\n'
  + 'Present — the section was located in the reviewed agreement with sufficient supporting evidence.\n\n'
  + 'Missing — the AI could not locate any evidence of this mandatory section in the reviewed documents.';

function MandatorySectionDetectionTable({ sections }: { sections: SpaMandatorySectionRow[] }): ReactNode {
  return (
    <div className="table-wrap">
      <table className="data-table" aria-label="Mandatory section detection">
        <thead>
          {/* Sprint 5 client feedback 2026-07-11 (Iter4 G3) — Source
              Document + Source Page columns COMBINED back into a
              single Source column carrying the concatenated
              "Investment Memo p.32" value. Iter3 #8/#13 had split
              them; the Iter4 client direction is the opposite —
              standardize on ONE Source column across DD / SPA /
              Risk. Preserved the classification tooltip / status
              tooltip work from Iter3 SPA #6 + #10. */}
          <tr>
            <th className="t-mono t-xs">#</th>
            <th>Section</th>
            <th data-tip={_CLASSIFICATION_TOOLTIP} style={{ whiteSpace: 'pre-line' }}>
              Classification <span style={{ opacity: 0.5, cursor: 'help' }}>ⓘ</span>
            </th>
            <th>Source</th>
            <th>Evidence</th>
            <th>Confidence</th>
            <th data-tip={_MANDATORY_STATUS_TOOLTIP} style={{ whiteSpace: 'pre-line' }}>
              Status <span style={{ opacity: 0.5, cursor: 'help' }}>ⓘ</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {sections.length === 0 ? (
            <tr><td colSpan={7} className="t-xs t-muted">Section detection has not yet completed.</td></tr>
          ) : (
            sections.map((s, idx) => {
              const classification = _mandatorySectionClassification(s.attribute_key);
              /* Iter6 #1 — canonical SourceChip. */
              return (
                <tr key={s.attribute_key}>
                  <td className="t-mono t-xs">{String(idx + 1).padStart(2, '0')}</td>
                  <td className="td-fw5">{s.display_name}</td>
                  <td>
                    <span className={`badge ${classification === 'Universal' ? 'badge-neutral' : 'badge-info'}`}>
                      {classification}
                    </span>
                  </td>
                  <td className="t-xs"><SourceChip doc={s.source_document ?? null} page={s.source_page} /></td>
                  <td className="t-xs">
                    {/* Sprint 5 client feedback 2026-07-11 (Iter3 SPA
                        #10) — Evidence cell now uses TruncatedCell
                        with alwaysShowView so every row exposes a
                        "View" button, even when the excerpt fits.
                        Analysts asked for a consistent affordance to
                        drill into full context regardless of length. */}
                    {s.evidence
                      ? <TruncatedCell title="Evidence" text={s.evidence} alwaysShowView />
                      : '—'}
                  </td>
                  <td className="t-mono t-xs">{s.confidence != null ? `${(s.confidence * 100).toFixed(0)}%` : '—'}</td>
                  <td>
                    {s.detected ? (
                      <span className="clause-detected">Present</span>
                    ) : (
                      <span className="clause-missing">Missing</span>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Section 3 — Investor Protection Clauses
// Sprint 5 client feedback 2026-06-23 (FE Third Doc Issue 5):
// the prior implementation rendered every clause in one wide dense
// table (8 columns × N rows). Redesigned into seven labelled sub-cards,
// each a compact horizontal table inside its own white card. Clauses
// bucket by attribute_key prefix; the seventh "Dispute Resolution"
// bucket catches the two dispute_resolution_* clauses the design's
// six labelled cards didn't include — silent-drop would understate
// the legal review.
// Sprint 5 client feedback 2026-07-12 (Task #733) — sub-card
// numbering renumbered 4.x → 3.x so the sub-cards match the parent
// section (Section 03) rather than the legacy §4 numbering.
// ─────────────────────────────────────────────────────────────────────────────

interface _ClauseGroup {
  num: string;
  title: string;
  match: (attributeKey: string) => boolean;
}

/* Sprint 5 client feedback 2026-07-11 (Iter3 SPA #12) — the §3.1
   sub-card was previously titled "Investor Protection Clauses",
   which duplicated the parent §3 heading and read as redundant.
   Client asked to rename to "Information Rights" so the sub-card
   labels the specific category of clauses inside. Note: this file
   still uses `4.x` numbering internally because the §3 → §2 shift
   from Iter3 SPA #5 happens at the composer level; the sub-card
   IDs are labels only, not routing keys. */
/* Sprint 5 client feedback 2026-07-11 (Iter3 SPA #14 / #15) — AI
   Analysis vocabulary is a fixed 9-label enum coming from
   computeSpa4Status. Both the column header (aggregate tooltip) and
   the individual badges (per-label tooltip) explain what each label
   means so reviewers can interpret classifications without opening
   the docs. Labels not in the map render without a tooltip. */
const _SPA4_AI_TOOLTIP =
  'AI Analysis: how the AI classifies this clause against benchmark market practice.\n\n'
  + 'Standard — aligns with common market practice.\n'
  + 'Investor-friendly — provides stronger investor protection than typical practice.\n'
  + 'Conservative — more restrictive than typical practice.\n'
  + 'Aggressive — favours one party more than typical practice.\n'
  + 'Inadequate — clause exists but does not sufficiently meet expected requirements.\n'
  + 'Watch — clause requires reviewer attention due to potential concerns.\n'
  + 'Not Present — required clause could not be identified in the agreement.\n'
  + 'Not Applicable — clause is not applicable to this deal / jurisdiction.\n'
  + 'Pass — clause satisfies the expected benchmark.';
/* Iter4 S9 — Status column header tooltip for §3 clause tables.
   Formatted as three paragraphs so the browser tooltip reads
   scannably. Describes the derived Pass / Medium / High / Critical
   tier (independent of the underlying AI Analysis enum). */
const _SPA4_STATUS_TOOLTIP =
  'Status — the derived severity tier for this clause.\n\n'
  + 'Pass — the clause satisfies the expected legal benchmark.\n'
  + 'Medium — the clause is acceptable but warrants reviewer attention (e.g. Conservative or Watch).\n'
  + 'High — the clause is more favourable to one party than standard practice or is inadequate.\n'
  + 'Critical — the clause is Not Present in the reviewed agreement and requires immediate action.';

const _SPA4_AI_LABEL_TOOLTIPS: Record<string, string> = {
  'Standard': 'Clause aligns with common market practice.',
  'Investor-friendly': 'Clause provides stronger protection for investors than typical market practice.',
  'Conservative': 'Clause is more restrictive than typical market standards.',
  'Aggressive': 'Clause is more favourable to one party than standard market practice.',
  'Inadequate': 'Clause exists but does not sufficiently satisfy expected legal requirements.',
  'Watch': 'Clause should be reviewed due to potential concerns or unclear evidence.',
  'Not Present': 'Required clause could not be identified in the reviewed agreement.',
  'Not Applicable': 'Clause is not applicable for the selected investment or jurisdiction.',
  'Pass': 'Clause satisfies the expected legal benchmark.',
};

/* Sprint 5 client feedback 2026-07-12 (Task #736) — per-row AI
 * Analysis reason tooltip. Composes:
 *   1. The label + one-line generic meaning (from _SPA4_AI_LABEL_TOOLTIPS).
 *   2. The extracted value on this row so the analyst sees what
 *      the AI is comparing against.
 *   3. The BE-emitted derivation_note when present — this is where
 *      the LLM records WHY the label was assigned (mismatch,
 *      missing evidence, etc.).
 *   4. A pointer to the Evidence column for the source excerpt.
 * The result is a multi-line native tooltip so no additional modal
 * is needed for the reason — hover reveals the reasoning inline. */
function _buildAiReasonTooltip(row: SpaClauseRow, label: string): string {
  const parts: string[] = [];
  const generic = _SPA4_AI_LABEL_TOOLTIPS[label];
  if (generic) parts.push(`${label} — ${generic}`);
  else parts.push(label);

  // Extracted value line — use whichever data_type field is populated.
  const extracted = ((): string | null => {
    if (row.raw_value != null && String(row.raw_value).trim()) return String(row.raw_value).trim();
    if (row.numeric_value != null) return `${row.numeric_value}${row.unit ? ' ' + row.unit : ''}`;
    if (typeof row.value === 'boolean') return row.value ? 'Yes' : 'No';
    return null;
  })();
  if (extracted) parts.push(`\nExtracted value: ${extracted}`);
  else if (label === 'Not Present') parts.push('\nNo extracted value — clause was not found in the reviewed documents.');

  // Derivation note carries the LLM/BE-emitted reason (mismatch,
  // missing evidence, deviation vs expected). Skip empty/placeholder
  // notes so the tooltip stays clean.
  const note = String(row.derivation_note ?? '').trim();
  if (note && note.toLowerCase() !== 'n/a' && note.toLowerCase() !== 'none') {
    parts.push(`\nReason: ${note}`);
  }

  if (row.evidence && String(row.evidence).trim()) {
    parts.push('\nSee the Evidence column for the source excerpt.');
  }
  return parts.join('\n');
}

/* Sprint 5 client feedback 2026-07-12 (Task #733) — subsection
 * numbering now aligns with the parent section (Section 03 —
 * Investor Protection Clauses). Prior 4.x scheme was a legacy
 * leftover from when this content lived under §04 in an earlier
 * template and created reader confusion because the SPA left-nav
 * shows "03 – Investor Protection Clauses" but the sub-cards were
 * labelled 4.1, 4.2, … Renamed constant SECTION_03_GROUPS →
 * SECTION_03_GROUPS and renumbered every sub-card 4.x → 3.x. */
const SECTION_03_GROUPS: _ClauseGroup[] = [
  { num: '3.1', title: 'Information Rights',           match: (k) => k.startsWith('clause_investor_protection') },
  { num: '3.2', title: 'Indemnification Clauses',      match: (k) => k.startsWith('clause_indemnification') },
  { num: '3.3', title: 'Anti-Dilution Clauses',        match: (k) => k.startsWith('clause_antidilution') },
  { num: '3.4', title: 'Board Representation Clauses', match: (k) => k.startsWith('clause_board') },
  { num: '3.5', title: 'Exit Rights Clauses',          match: (k) => k.startsWith('clause_exit') },
  { num: '3.6', title: 'Confidentiality Clauses',      match: (k) => k.startsWith('clause_confidentiality') },
  { num: '3.7', title: 'Dispute Resolution Clauses',   match: (k) => k.startsWith('clause_dispute') },
];

/* Doc spec colour map for §04:
     Present / Strong / Pass / Approve / Standard / Investor-friendly → green
     Adequate                                                          → blue
     Watch / High                                                      → orange
     Critical / Reject / Inadequate / Aggressive                       → red
   The existing clauseCategoryBadge() lumps Adequate into pass (green);
   §04 wants it distinct so analysts can scan "Adequate but not Strong"
   at a glance. */
function s04CategoryBadge(cat: string | null | undefined): { className: string; label: string } {
  if (!cat) return { className: 'badge badge-neutral', label: '—' };
  const c = cat.trim();
  if (c === 'Adequate') return { className: 'badge badge-info', label: c };
  if (c === 'Present')  return { className: 'badge badge-pass', label: c };
  // Sprint 5 client feedback 2026-07-04 — Tag-Along/Co-Sale "Present
  // by reference": SPA §4.9 references the ROFR/Co-Sale Agreement as
  // a delivered doc but the agreement itself isn't uploaded. Renders
  // as info (blue) so the reviewer sees the soft signal without a
  // red-badge alarm; severity is Low downstream.
  if (c === 'Present by reference') {
    return { className: 'badge badge-info', label: 'Present by reference' };
  }
  // "Cannot Assess" chip — Medium severity downstream, treated as a
  // watchful info signal on the badge.
  if (c === 'Cannot Assess') {
    return { className: 'badge badge-watch', label: 'Cannot Assess' };
  }
  /* Iter9 #3 — High elevated to red (was orange) alongside Critical
   * and the reject/aggressive/inadequate cluster; Watch stays amber
   * (Medium tier). Recommendation added as blue. */
  if (['Critical', 'High', 'Reject', 'Inadequate', 'Aggressive'].includes(c)) {
    return { className: 'badge badge-fail', label: c };
  }
  if (['Watch', 'Medium'].includes(c)) {
    return { className: 'badge badge-watch', label: c };
  }
  if (['Recommendation'].includes(c)) {
    return { className: 'badge badge-info', label: c };
  }
  if (['Pass', 'Strong', 'Approve', 'Standard', 'Investor-friendly'].includes(c)) {
    return { className: 'badge badge-pass', label: c };
  }
  return { className: 'badge badge-neutral', label: c };
}

/**
 * Sprint 5 client feedback 2026-07-02 — §4 status/text consistency
 * rescue. Analysts reported rows where the Value cell reads a
 * negative signal ("No" / "Not present" / raw prose like "Drag-Along
 * Rights not present in Voting Agreement") while the Status column
 * still rendered a positive badge like "Standard". Root cause: the
 * BE emits category_assigned independently of the raw_value payload,
 * so a stale / mis-inferred positive category can survive a genuine
 * boolean=false extraction.
 *
 * Rule: when the Value display carries an unambiguous absence signal
 * — data_type=boolean with value=false, OR raw_value contains
 * "not present", "not detected", "missing", "n/a", "absent",
 * "no drag", "no tag", etc — override the badge to a neutral
 * "Not present" chip so the row reads as internally consistent.
 * Never applied when the Value is genuinely populated (Yes /
 * numeric / narrative value); positive categories flow through.
 */
const _ABSENCE_MARKERS = [
  'not present',
  'not detected',
  'not detect',
  'not found',
  'not identified',
  'no drag',
  'no tag',
  'no such',
  'absent from',
  'missing from',
  'not applicable',
  'n/a',
  'none detected',
  'none identified',
];

/**
 * Sprint 5 client feedback 2026-07-03 — §4.5 Exit Rights bug: every
 * row (Drag-Along / Tag-Along / ROFR on Transfer) came back with
 * data_type='json_object' + json_value.present === false, but the
 * status badges rendered as "Investor-Friendly" / "Conservative" /
 * "Standard" — positive labels on absent clauses. Root cause: the
 * raw_value serialisation for json_object is a JSON blob containing
 * "present": false, which _ABSENCE_MARKERS ("not present" / "not
 * detected" / etc.) can't match. The boolean check (data_type ===
 * 'boolean') also doesn't apply.
 *
 * Detect the negative-present json_object case explicitly and route
 * it into the same "Not present" chip we already use for boolean
 * false + absence markers.
 */
function _jsonObjectMarksAbsent(row: SpaClauseRow): boolean {
  if (row.data_type !== 'json_object') return false;
  const jv = row.json_value as Record<string, unknown> | null | undefined;
  if (!jv || typeof jv !== 'object') return false;
  // Explicit `present: false` — the most common shape for §4.5 rows.
  if (jv.present === false) return true;
  // Some clauses use `detected` instead of `present`.
  if ((jv as { detected?: unknown }).detected === false) return true;
  return false;
}

/* Sprint 5 client feedback 2026-07-08 — retained in module for
   reference (board-representation upgrade + absence-marker heuristics
   from earlier tickets). The §4 sub-card renderer now routes through
   computeSpa4Status instead. If those pre-taxonomy heuristics need to
   resurface, they should be promoted to backend synthesis where they
   can shape category_assigned directly. */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function s04ConsistentStatusBadge(row: SpaClauseRow): { className: string; label: string } {
  const rawText = String(row.raw_value ?? '').toLowerCase();
  const isNegativeBoolean = row.data_type === 'boolean' && row.value === false;
  const hasAbsenceMarker = rawText
    ? _ABSENCE_MARKERS.some((m) => rawText.includes(m))
    : false;
  const jsonMarksAbsent = _jsonObjectMarksAbsent(row);
  if (isNegativeBoolean || hasAbsenceMarker || jsonMarksAbsent) {
    return { className: 'badge badge-watch', label: 'Not present' };
  }
  /**
   * Sprint 5 client feedback 2026-07-02 — Board Representation upgrade.
   * The EH2 SPA §4.6 at p.17-18 explicitly names 9 board members
   * ("the Board shall be composed of David Danielson, Dipender Saluja,
   * ..., Shayle Kann"). The BE was tagging category_assigned as
   * "Conditional" because the LLM heuristic saw a designated seat
   * right rather than a definitive present state. When the evidence
   * carries an unambiguous "Board shall be composed of {names}" or
   * "authorized size of the Board shall be N" phrase, upgrade
   * Conditional → Present so §10 reflects the operative clause
   * strength.
   *
   * Rule stays scoped to clauses whose attribute_key mentions
   * "board" AND whose category is Conditional / Weak / Watch —
   * never touches Present / Standard / Missing rows.
   */
  const attrKey = String(row.attribute_key ?? '').toLowerCase();
  const currentCat = (row.category_assigned ?? '').trim();
  const rawEvidence = String(row.evidence ?? '').toLowerCase();
  if (
    attrKey.includes('board')
    && ['Conditional', 'Weak', 'Watch'].includes(currentCat)
  ) {
    const combined = `${rawText} | ${rawEvidence}`;
    const namesPresent = (
      /board (shall|will) be composed of/.test(combined)
      || /board (shall|will) consist of/.test(combined)
      || /authori[sz]ed size of the board (shall|will) be/.test(combined)
      || /board of directors (shall|will) (be composed of|consist of)/.test(combined)
    );
    if (namesPresent) {
      return { className: 'badge badge-pass', label: 'Present' };
    }
  }
  return s04CategoryBadge(row.category_assigned);
}
/* Reference-only to satisfy noUnusedLocals — see comment above.
   Retained in module for potential resurface as BE synthesis logic. */
void s04ConsistentStatusBadge;

/* ─────────────────────────────────────────────────────────────
   Sprint 5 client feedback 2026-07-08 (FE Ticket — SPA §4 Not
   Present taxonomy) — shared computeSpa4Status utility.

   ONE source of truth for §4 row → (AI Analysis label + Status
   badge). SPA §4 renderer, Risk §11 severity cascade, and any
   future §4-consuming surface all call this so the two tables
   can never disagree on the same underlying row.

   Contract:
     Input:  SpaClauseRow (or any shape carrying category_assigned,
             value, source_document, source_page, evidence)
     Output: {
       aiAnalysisLabel: string,   ← "Standard" | "Aggressive" | ...
       aiAnalysisClass: string,   ← badge class for AI Analysis column
       status:          string,   ← "Pass" | "Medium" | "High" | "Critical"
       statusClass:     string,   ← badge class for Status column
     }

   Historical defensive read (FIX 5, FIX 8):
     * Stored category = "Watch" AND value ∈ MISSING_SET →
       display as "Not Present" (badge-fail)
     * Stored category = "Not present" (lowercase p, legacy
       canonical-doc-missing path) → display as "Not Present"

   Post-fix runs write "Not Present" directly; the defensive
   read is a no-op on new rows.

   Status mapping (FIX 5 § "Status column"):
     Not Present                              → Critical
     Inadequate / Aggressive                  → High
     Conservative / Watch                     → Medium
     Standard / Investor-friendly / Pass /
       Not Applicable                         → Pass
     <anything else>                          → Medium + console warn
   ─────────────────────────────────────────────────────────── */

const _MISSING_SET_SPA4 = new Set([
  '', '—', '-', 'not found', 'not detected',
  'not present', 'null', 'none', 'n/a',
]);

/* Sprint 5 client feedback 2026-07-08 (FE Ticket — Path B
   coercion). EXTRACTION_FAILURE_SET differs from _MISSING_SET_SPA4
   by ONE element: it excludes "not present". Rationale — "Not
   Present" is the terminal absence state and always routes to
   Critical via Case A / C above. Values that fall in this set
   represent the LLM's failure to extract a concrete clause value
   even though Qdrant returned a chunk (source populated). Those
   rows deserve analyst review → Watch / Medium, not Critical. */
const _EXTRACTION_FAILURE_SET = new Set([
  '', '—', '-', 'not found', 'not detected',
  'null', 'none', 'n/a',
]);
const _EMPTY_SET_SPA4 = new Set(['', '—', '-']);

function _spa4NormaliseValueForMissingCheck(raw: unknown): string {
  return String(raw ?? '').trim().toLowerCase();
}

/* Source-populatedness check: a row's "Source" is composed of
   source_document + source_page (see ClauseSubCard render). The
   row is considered "Qdrant returned nothing" only when BOTH
   fields are empty (source_document null / blank / dash marker,
   AND source_page null). If either citation is present, Qdrant
   hit something — Path B may apply. */
function _spa4SourceIsEmpty(row: SpaClauseRow): boolean {
  const docRaw = String(row.source_document ?? '').trim().toLowerCase();
  const docEmpty = _EMPTY_SET_SPA4.has(docRaw);
  const pageEmpty = row.source_page == null;
  return docEmpty && pageEmpty;
}

interface Spa4ComputedStatus {
  aiAnalysisLabel: string;
  aiAnalysisClass: string;
  status: 'Pass' | 'Medium' | 'High' | 'Critical';
  statusClass: string;
}

export function computeSpa4Status(row: SpaClauseRow): Spa4ComputedStatus {
  // Step 1 — read raw category. Defensive: null / undefined / empty
  // string all trigger the "no category set" branch below.
  const rawCategoryStr = (row.category_assigned ?? '').toString().trim();
  const rawCategoryLower = rawCategoryStr.toLowerCase();

  // Value MISSING_SET check for the defensive read below.
  const normValue = _spa4NormaliseValueForMissingCheck(
    row.value ?? row.raw_value ?? '',
  );
  const valueMissing = _MISSING_SET_SPA4.has(normValue);

  // Sprint 5 client feedback 2026-07-08 — Path B inputs.
  // EXTRACTION_FAILURE_SET is _MISSING_SET_SPA4 minus "not present"
  // (which is terminal absence, routed via Case A / C).
  const valueInExtractionFailure = _EXTRACTION_FAILURE_SET.has(normValue);
  const sourceEmpty = _spa4SourceIsEmpty(row);

  // Step 2 — historical defensive read + Path B precedence.
  //
  // Precedence (top → bottom, first match wins):
  //   Case A — Stored = "Not Present" (canonical) → Critical
  //   Case C — Stored = "not present" (legacy lowercase) → Critical
  //   Path 3 — Source ∈ EMPTY_SET → Not Present (Qdrant returned
  //            nothing; the row is a canonical absence). This
  //            subsumes the legacy "Watch + missing value" and
  //            "empty category + missing value" branches when
  //            Source is also empty.
  //   Path B — Value ∈ EXTRACTION_FAILURE_SET AND Source populated
  //            → Watch / Medium (Qdrant hit a chunk, LLM failed
  //            to extract a value → analyst review).
  //   Case E — Otherwise → use stored label as-is.
  //
  // Legacy "Watch" + missing value (Source populated) rows used
  // to render as "Not Present"; under Path B they now render as
  // "Watch". Byte-identical guardrails RG-1 / RG-2 are preserved:
  // populated-Value rows still render Case E; Source-empty rows
  // still render "Not Present".
  let aiAnalysisLabel: string;
  if (rawCategoryLower === 'not present') {
    // Coerce "not present" / "Not present" → canonical "Not Present"
    aiAnalysisLabel = 'Not Present';
  } else if (sourceEmpty) {
    // Qdrant returned nothing — canonical absence regardless of
    // whatever category / value the row carries. When category
    // is populated but Source is empty, we still route to Not
    // Present per Path 3 (RG-2 byte-identical guarantee).
    aiAnalysisLabel = valueMissing || !rawCategoryStr ? 'Not Present' : rawCategoryStr;
  } else if (valueInExtractionFailure) {
    // Path B (NEW) — Qdrant hit a chunk (Source populated), but
    // the LLM could not extract / validate a concrete Value. This
    // is neither terminal absence nor a validated directional
    // judgement; treat it as analyst-review-required (Watch /
    // Medium) and log an observability signal so a follow-up BE
    // ticket can route it deterministically in the LLM path.
    aiAnalysisLabel = 'Watch';
    const clauseName = row.display_name || row.attribute_key || '(unknown)';
    const rawVal = String(row.value ?? row.raw_value ?? '');
    // eslint-disable-next-line no-console
    console.warn(
      `Path B — ${clauseName}: Qdrant hit, AI could not extract Value ('${rawVal}'). Coerced to Watch.`,
    );
  } else if (!rawCategoryStr) {
    // Empty category, Value populated, Source populated — no
    // directional judgement from BE. Historically rendered as an
    // unlabelled pill; default to Watch to surface for review.
    aiAnalysisLabel = 'Watch';
  } else {
    // Case E — Source populated, Value populated, stored category
    // set. Use as-is (byte-identical to prior ticket per RG-1).
    aiAnalysisLabel = rawCategoryStr;
  }

  // Step 3 — AI Analysis badge class (matches FIX 5 § "AI Analysis").
  const aiClassMap: Record<string, string> = {
    'Standard':          'badge badge-pass',
    'Pass':              'badge badge-pass',
    'Investor-friendly': 'badge badge-pass',
    'Conservative':      'badge badge-pass',
    'Not Applicable':    'badge badge-neutral',
    'Watch':             'badge badge-watch',
    'Aggressive':        'badge badge-fail',
    'Inadequate':        'badge badge-fail',
    'Not Present':       'badge badge-fail',
  };
  const aiAnalysisClass = aiClassMap[aiAnalysisLabel] ?? 'badge badge-neutral';

  /* Sprint 5 client feedback 2026-07-12 (Iter9 #3) — Critical
   * retired across the SPA report. "Not Present" now maps to High
   * (was Critical); Critical is kept in the enum only as a legacy
   * pass-through folded to badge-fail. Aggressive/Inadequate keep
   * their High mapping. */
  const statusMap: Record<string, 'Pass' | 'Medium' | 'High' | 'Critical'> = {
    'Not Present':       'High', // Iter9 #3: was Critical
    'Inadequate':        'High',
    'Aggressive':        'High',
    'Conservative':      'Medium',
    'Watch':             'Medium',
    'Standard':          'Pass',
    'Investor-friendly': 'Pass',
    'Pass':              'Pass',
    'Not Applicable':    'Pass',
  };
  let status: 'Pass' | 'Medium' | 'High' | 'Critical';
  if (aiAnalysisLabel in statusMap) {
    status = statusMap[aiAnalysisLabel];
  } else {
    status = 'Medium';
    // Safety net per FIX 5 § "Status column".
    // eslint-disable-next-line no-console
    console.warn(`[SPA §4] unknown AI Analysis label: "${aiAnalysisLabel}"`);
  }

  const statusClassMap: Record<'Pass' | 'Medium' | 'High' | 'Critical', string> = {
    'Pass':     'badge badge-pass',
    'Medium':   'badge badge-watch',
    'High':     'badge badge-fail',
    'Critical': 'badge badge-fail', // legacy compat; unreachable via statusMap
  };
  const statusClass = statusClassMap[status];

  return { aiAnalysisLabel, aiAnalysisClass, status, statusClass };
}


function ClauseSubCard({ group, rows }: { group: _ClauseGroup; rows: SpaClauseRow[] }): ReactNode {
  if (rows.length === 0) return null;
  return (
    <div className="s04-card" id={`s04-${group.num.replace('.', '-')}`}>
      <div className="s04-card-h">
        <span className="s04-card-num">{group.num}</span>
        <span className="s04-card-title">{group.title}</span>
      </div>
      <div className="table-wrap">
        <table className="data-table s04-table" aria-label={group.title}>
          <thead>
            {/* Sprint 5 client feedback 2026-07-08 (FE Ticket — SPA §4
                Not Present taxonomy) — the old single "Status" column
                is split into two: "AI Analysis" (the enum badge —
                Standard / Aggressive / … / Not Present) and "Status"
                (the derived Pass / Medium / High / Critical tier).
                Both computed from computeSpa4Status so a single source
                of truth drives every downstream surface.

                Sprint 5 client feedback 2026-07-11 (Iter3 SPA #8/#13,
                #14, #17) — Source column split into Source Doc + Page
                to match the standard adopted across DD / SPA / Risk.
                Evidence cell always renders the "View" button so
                analysts can drill into full excerpt regardless of
                length. AI Analysis column tooltip explains the
                9-label vocabulary. Both status columns center-aligned
                for a cleaner scan. */}
            <tr>
              <th>Clause</th>
              <th>Value</th>
              <th>Source</th>
              <th>Evidence</th>
              <th className="s04-status-col" data-tip={_SPA4_AI_TOOLTIP} style={{ textAlign: 'center', whiteSpace: 'pre-line' }}>
                AI Analysis <span style={{ opacity: 0.5, cursor: 'help' }}>ⓘ</span>
              </th>
              <th className="s04-status-col" data-tip={_SPA4_STATUS_TOOLTIP} style={{ textAlign: 'center', whiteSpace: 'pre-line' }}>
                Status <span style={{ opacity: 0.5, cursor: 'help' }}>ⓘ</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => {
              /* Shared util is the sole source of truth for both AI
                 Analysis (enum) and Status (tier) — preserves the
                 invariant that both columns agree with the row's
                 stored category_assigned + value state. Legacy
                 board-upgrade / absence-marker heuristics from the
                 s04ConsistentStatusBadge helper are pre-taxonomy and
                 could produce non-enum labels ("Present" / "Not
                 present"); dropping them here in favour of the util
                 also fixes rows that historically rendered outside
                 the closed 9-label vocabulary. */
              const computed = computeSpa4Status(c);
              /* Sprint 5 client feedback 2026-07-11 (Iter3 SPA #16)
                 — long clause values (Board Rep, Reserved Matters,
                 Information Rights) overflow the cell. Compute
                 rendered value string once so both the inline label
                 (via TruncatedCell) and the modal body see the same
                 text. */
              const clauseValStr = clauseValueDisplay(c);
              const clauseValLong = typeof clauseValStr === 'string' && clauseValStr.length > 60;
              return (
                <tr key={c.attribute_key}>
                  <td className="s04-clause-name">{c.display_name}</td>
                  <td className="s04-clause-val">
                    {/* Format Phase 5 — the short branch also needs
                        markdown-aware rendering; long branch is fixed
                        by Phase 1 TruncatedCell fallback. */}
                    {clauseValLong
                      ? <TruncatedCell title="Value" text={clauseValStr} />
                      : (typeof clauseValStr === 'string'
                          ? <FormattedText text={clauseValStr} inline />
                          : clauseValStr)}
                  </td>
                  <td className="s04-clause-src t-xs">
                    {/* Iter6 #1 — canonical SourceChip. */}
                    <SourceChip doc={c.source_document ?? null} page={c.source_page} />
                  </td>
                  {/* Sprint 5 client feedback 2026-07-03 — evidence
                      may carry LLM-emitted markdown (**bold**, *ital*).
                      Attribute contexts can't render HTML so strip the
                      markers for the tooltip; the visible cell content
                      routes through FormattedText so **bold** renders
                      as real bold instead of literal asterisks. */}
                  <td
                    className="s04-clause-ev"
                    data-tip={c.evidence ? cleanTooltip(c.evidence) : undefined}
                  >
                    {/* Sprint 5 client feedback 2026-07-12 (Task #735) —
                        Evidence popup was rendering the modal body via
                        the same inline FormattedText the clamp cell
                        uses, so multi-paragraph evidence excerpts
                        collapsed to a single soft line and read as
                        truncated. Passing an explicit modalBody with
                        block-level FormattedText restores paragraph
                        breaks, bullet lists, and quoted passages —
                        analyst now sees the full extracted clause
                        with proper structure and scrollable overflow.
                        The clamp cell inline preview is unchanged. */}
                    {c.evidence
                      ? <TruncatedCell
                          title="Evidence"
                          text={stripMarkdown(c.evidence)}
                          alwaysShowView
                          modalBody={<FormattedText text={c.evidence} />}
                        >
                          <FormattedText text={c.evidence} inline />
                        </TruncatedCell>
                      : '—'}
                  </td>
                  <td className="s04-clause-status" style={{ textAlign: 'center' }}>
                    {/* Sprint 5 client feedback 2026-07-12 (Task #736)
                        — AI Analysis tooltip now explains WHY THIS
                        specific row was assigned the label (extracted
                        value + derivation note + evidence pointer),
                        not just what the label means in general.
                        Analyst can hover to see the reasoning without
                        opening the Evidence modal.
                        Delivered via data-tip (not `title`) so it uses the
                        instant app tooltip instead of the ~1s native popup. */}
                    <span
                      className={computed.aiAnalysisClass}
                      data-tip={_buildAiReasonTooltip(c, computed.aiAnalysisLabel)}
                      style={{ cursor: 'help' }}
                    >
                      {computed.aiAnalysisLabel}
                    </span>
                  </td>
                  <td className="s04-clause-status" style={{ textAlign: 'center' }}>
                    <span className={computed.statusClass}>{computed.status}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function InvestorProtectionClauses({ clauses }: { clauses: SpaClauseRow[] }): ReactNode {
  if (clauses.length === 0) {
    return (
      <div className="s04-empty t-xs t-muted">
        Clause extraction has not yet completed.
      </div>
    );
  }
  // Bucket clauses by attribute_key prefix; collect un-grouped clauses
  // into an "Other" trailing card so nothing is silently dropped if
  // SPA.yaml adds a new clause whose prefix isn't in SECTION_03_GROUPS.
  const buckets: { group: _ClauseGroup; rows: SpaClauseRow[] }[] = SECTION_03_GROUPS.map((g) => ({
    group: g,
    rows: [],
  }));
  const other: SpaClauseRow[] = [];
  for (const c of clauses) {
    const idx = SECTION_03_GROUPS.findIndex((g) => g.match(c.attribute_key));
    if (idx === -1) other.push(c);
    else buckets[idx].rows.push(c);
  }
  return (
    <div className="s04-stack">
      {buckets.map(({ group, rows }) => (
        <ClauseSubCard key={group.num} group={group} rows={rows} />
      ))}
      {other.length > 0 && (
        <ClauseSubCard
          key="other"
          group={{ num: '4.8', title: 'Other Clauses', match: () => true }}
          rows={other}
        />
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Section 5 — Issue Identification & Classification
// ─────────────────────────────────────────────────────────────────────────────

/* Sprint 5 client feedback 2026-07-11 (Iter3 SPA #20) — derive a
   user-friendly Section Number ("§2.3", "§3.1", "§3.5", …) for each
   finding based on its attribute_key. Falls back to the finding's
   own flag_id namespace when the key is unrecognised. Note the base
   section numbers reflect the post-Iter3 composer shift: §3 SPA
   Investor Protection → §3 (was §4), mandatory sections → §2. */
function _sectionRefForAttrKey(attributeKey: string | null | undefined): string {
  const k = (attributeKey ?? '').toLowerCase();
  if (!k) return '—';
  if (k.startsWith('section_parties')) return '§2.1';
  if (k.startsWith('section_recitals')) return '§2.2';
  if (k.startsWith('section_purchase_price')) return '§2.3';
  if (k.startsWith('section_representations')) return '§2.4';
  if (k.startsWith('section_governing_law')) return '§2.5';
  if (k.startsWith('section_signatures')) return '§2.6';
  if (k.startsWith('clause_investor_protection')) return '§3.1';
  if (k.startsWith('clause_indemnification')) return '§3.2';
  if (k.startsWith('clause_antidilution')) return '§3.3';
  if (k.startsWith('clause_board')) return '§3.4';
  if (k.startsWith('clause_exit')) return '§3.5';
  if (k.startsWith('clause_confidentiality')) return '§3.6';
  if (k.startsWith('clause_dispute')) return '§3.7';
  return '—';
}

/* Sprint 5 client feedback 2026-07-11 (Iter4 S10) — humanise raw
   attribute_keys like "clause_antidilution_esop_carveout_pct" into
   reader-friendly labels ("Anti-Dilution: ESOP Carve-out %"). The
   clauseNameByKey map is the preferred source (comes from
   SpaClauseRow.display_name which the BE curates). When the key
   isn't in the map (e.g. mandatory section refs, or synthesised
   findings), fall back to a general-purpose splitter:
     * strip the leading namespace (clause_, section_, spa_)
     * split on '_' → title-case each token
     * expand a small set of legal-domain acronyms (ROFR, ESOP, LOI,
       MOU, R&W) so the output reads correctly
     * append "%" when the original ended in "_pct" (percentage)
   Handles null / empty via the caller's fallback of '—'. */
const _ACRONYM_FIX: Record<string, string> = {
  rofr: 'ROFR',
  esop: 'ESOP',
  loi: 'LOI',
  mou: 'MOU',
  'r&w': 'R&W',
  llc: 'LLC',
  ip: 'IP',
  ipo: 'IPO',
  poi: 'POI',
  poe: 'POE',
  eu: 'EU',
  us: 'US',
  usd: 'USD',
  ai: 'AI',
  antidilution: 'Anti-Dilution',
};
function _humaniseAttributeKey(
  attrKey: string | null | undefined,
  clauseNameByKey?: Map<string, string>,
): string {
  if (!attrKey) return '—';
  if (clauseNameByKey?.has(attrKey)) return clauseNameByKey.get(attrKey)!;
  let stripped = attrKey.replace(/^(clause_|section_|spa_)/i, '');
  const isPct = /_pct$/i.test(stripped);
  if (isPct) stripped = stripped.replace(/_pct$/i, '');
  const parts = stripped.split(/_/).map((tok) => {
    const lower = tok.toLowerCase();
    if (_ACRONYM_FIX[lower]) return _ACRONYM_FIX[lower];
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  });
  let out = parts.join(' ');
  if (isPct) out += ' %';
  return out;
}

function IssuesTable({
  issues,
  clauseNameByKey,
}: {
  issues: SpaIssueRow[];
  clauseNameByKey?: Map<string, string>;
}): ReactNode {
  return (
    <div className="table-wrap">
      <table className="data-table" aria-label="Issues consolidated list">
        {/* Sprint 5 client feedback 2026-07-11 (Iter3 SPA #18, #19,
            #20) — column set restructured:
              * Finding ID column renders sequential F-01, F-02, … F-nn
                instead of the internal SPA-CLAUSE-… identifiers, which
                are opaque to reviewers. Original flag_id survives as
                the React key so cross-report linkage still works.
              * Source Page column dropped (redundant with the source
                citation already in §3 / §4 tables).
              * Responsible column dropped (mostly "—" or "Legal";
                low information density).
              * NEW Section Number column shows §2.x / §3.x based on
                the finding's attribute_key so reviewers can jump
                straight to the originating clause.
            Column widths rebalanced. */}
        <colgroup>
          <col style={{ width: '8%' }} />
          <col style={{ width: '18%' }} />
          <col style={{ width: '9%' }} />
          <col style={{ width: '9%' }} />
          <col style={{ width: '36%' }} />
          <col style={{ width: '20%' }} />
        </colgroup>
        <thead>
          <tr>
            <th>Finding ID</th>
            <th>Clause / Section</th>
            <th>Section №</th>
            <th>Severity</th>
            <th>Finding</th>
            <th>Action Required</th>
          </tr>
        </thead>
        <tbody>
          {issues.length === 0 ? (
            <tr><td colSpan={6} className="t-pass t-xs">No issues raised — agreement passes all checks.</td></tr>
          ) : (
            issues.map((i, idx) => (
              <tr key={i.flag_id}>
                <td className="t-mono t-xs" data-tip={i.flag_id}>
                  F-{String(idx + 1).padStart(2, '0')}
                </td>
                {/* Sprint 5 client feedback 2026-07-11 (Iter4 S10) —
                    surface the human display name (from §4 clause
                    map) instead of the raw attribute_key. Falls
                    back to the deterministic humaniser when the row
                    doesn't correspond to a §4 clause (mandatory
                    sections, synthesised findings). Original key
                    is retained on hover-tooltip for cross-report
                    lookups. */}
                <td className="td-fw5" data-tip={i.attribute_key ?? undefined}>
                  {_humaniseAttributeKey(i.attribute_key, clauseNameByKey)}
                </td>
                <td className="t-mono t-xs">{_sectionRefForAttrKey(i.attribute_key)}</td>
                {/* Iter9 #3 — Critical retired. Legacy Critical rows
                    render as "High" (folded via _foldSevForDisplay).
                    Recommendation is a new blue-badged tier below Low. */}
                <td><span className={`badge ${SEVERITY_BADGE[i.severity] ?? 'badge-neutral'}`}>{_foldSevForDisplay(i.severity)}</span></td>
                <td className="t-xs">
                  {/* FE Ticket 1 2026-07-07 — issue descriptions can run
                      to a paragraph. Clamp + View. */}
                  {i.description
                    ? <TruncatedCell title="Finding" text={i.description} />
                    : <span className="t-muted">—</span>}
                </td>
                <td className="t-xs">
                  {/* Format Phase 5 — LLM-authored action_required may
                      contain **bold** markers; route through FormattedText
                      inline so no literal asterisks leak. */}
                  {i.action_required
                    ? <FormattedText text={String(i.action_required)} inline />
                    : ''}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Section 6 — Legal Findings Summary
// ─────────────────────────────────────────────────────────────────────────────

function LegalFindingsSummary({
  signoff,
  overallStatus,
  synth,
}: {
  signoff: SpaSignoffSnapshot | null;
  overallStatus: string | null;
  synth: SpaLegalFindingsSynth | null;
}): ReactNode {
  if (signoff == null) {
    // Sprint 5 client feedback 2026-06-22: Section 06 used to show only
    // a generic "Sign-off Pending" card. Now we render the AI-synthesised
    // Legal Findings narrative (3 paragraphs covering structural
    // completeness, investor-protection posture, recommendation
    // reasoning) when the BE produced one. Falls back to the original
    // placeholder when the synth is missing (older SPA runs predate
    // this field).
    const hasSynth = synth != null && Array.isArray(synth.paragraphs) && synth.paragraphs.length > 0;
    return (
      <div className="result-card is-watch">
        <div className="result-card-strip"></div>
        <div className="result-card-body">
          {/* Sprint 5 client feedback 2026-07-11 (Iter3 SPA #21) —
              "Sign-off Pending" is a critical approval milestone but
              was rendering as an inline .result-pill that blended into
              the surrounding prose. Promote to a large, high-contrast
              banner-style pill with a pulsing dot so reviewers can
              spot at a glance that the report is awaiting sign-off.
              Colour uses --watch tokens which map to amber in both
              themes. */}
          <span
            className="badge badge-watch"
            style={{
              fontSize: '0.9375rem',
              fontWeight: 700,
              padding: '6px 14px',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 'var(--s3)',
            }}
          >
            <span className="pulse-dot" />
            Sign-off Pending
          </span>
          {hasSynth ? (
            <>
              {synth!.headline ? (
                <p className="result-summary-lg" style={{ fontWeight: 600, marginBottom: 'var(--s3)' }}>
                  {synth!.headline}
                </p>
              ) : null}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s3)' }}>
                {synth!.paragraphs.map((p, i) => (
                  <p key={i} className="result-summary-lg" style={{ marginBottom: 0 }}>
                    {p}
                  </p>
                ))}
              </div>
              <p className="t-xs t-muted" style={{ marginTop: 'var(--s4)', fontStyle: 'italic' }}>
                {synth!.degraded
                  ? 'AI synthesis was unavailable for this run — the summary above is a deterministic fallback derived from the section + clause findings. '
                  : 'AI-generated summary based on the extracted sections, clause analysis, and severity findings. '}
                The system has computed an <strong>{overallStatus ?? '—'}</strong> recommendation; a Reviewer or
                Compliance Officer must submit a formal sign-off before this Legal Review is considered final.
              </p>
            </>
          ) : (
            <p className="result-summary-lg">
              This Legal Review has not yet been signed off. The system has computed an{' '}
              <strong>{overallStatus ?? '—'}</strong> recommendation. A Reviewer or
              Compliance Officer must submit a formal sign-off before this Legal
              Review is considered final. Once submitted, this section will reflect
              the reviewer's formal decision, comments, and any override status.
            </p>
          )}
        </div>
      </div>
    );
  }

  const overrideTag = signoff.is_override ? (
    <span className="badge badge-watch" style={{ marginLeft: 8 }}>OVERRIDE</span>
  ) : null;

  return (
    <>
      <div className="table-wrap">
        <table className="data-table" aria-label="Sign-off summary">
          <tbody>
            <tr><td className="td-fw5">Reviewer Decision</td><td><span className={`badge ${STATUS_BADGES[signoff.decision] ?? 'badge-neutral'}`}>{signoff.decision}</span>{overrideTag}</td></tr>
            <tr><td className="td-fw5">System Recommendation at Sign-off</td><td><span className={`badge ${STATUS_BADGES[signoff.system_overall_status] ?? 'badge-neutral'}`}>{signoff.system_overall_status}</span></td></tr>
            <tr><td className="td-fw5">Signed By</td><td>{signoff.signed_by_user_name ?? '—'}{signoff.signed_by_user_role ? ` · ${signoff.signed_by_user_role}` : ''}</td></tr>
            <tr><td className="td-fw5">Signed At</td><td className="t-xs">{formatDateTime(signoff.signed_at)}</td></tr>
            <tr><td className="td-fw5">Reviewer Comments</td><td style={{ whiteSpace: 'pre-wrap' }}>
              {/* FE Ticket 1 2026-07-07 — reviewer comments are freeform
                  and often multi-paragraph; clamp + View. */}
              {signoff.comments
                ? <TruncatedCell title="Reviewer Comments" text={signoff.comments} />
                : <span className="t-muted">— no comments —</span>}
            </td></tr>
          </tbody>
        </table>
      </div>
      {signoff.is_override && (
        <p className="t-xs t-muted" style={{ marginTop: 'var(--s3)' }}>
          Note: The reviewer's decision differs from the system's automated recommendation.
          This OVERRIDE is recorded in the audit trail (Section 7) under the
          <code className="t-mono"> SIGNOFF_OVERRIDE</code> event type.
        </p>
      )}
    </>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Section 7 — Audit Trail
//
// Sprint 5 client feedback 2026-07-11 (Iter3 SPA #22) — REMOVED from
// the SPA report UI. The full audit trail (including SIGNOFF /
// SIGNOFF_OVERRIDE events) is already included in the downloaded DOCX
// / PDF report; duplicating it in the UI created noise. Historical
// AuditTrail component removed with its call site in buildSpaSections
// composer above.
// ─────────────────────────────────────────────────────────────────────────────


// ─────────────────────────────────────────────────────────────────────────────
/* ─────────────────────────────────────────────────────────────
   Sprint 5 client feedback 2026-07-08 (FE Ticket — SPA §5 §4-
   source severity from computeSpa4Status).

   §5's §4-sourced issues now derive their severity from the
   shared computeSpa4Status util. Pass rows are excluded from §5.
   §4-sourced group ordered by (severity → attribute_key ascending).
   Non-§4 issues are untouched.

   Source-of-truth detection: an issue is §4-sourced when
     * attribute_key starts with "clause_" (the direct link
       from SPA severity_rules over clause_* attributes), OR
     * flag_id begins with "SPA-CLAUSE-" (the SPA.yaml
       severity_rule template used for these clauses)

   Both signals are already emitted by the BE severity engine;
   the FE just consumes.

   Byte-identical guarantee for non-§4 issues: they pass through
   unchanged (same order, same severity, same text). See RG-1.
   ─────────────────────────────────────────────────────────── */

/* Sprint 5 client feedback 2026-07-12 (Iter9 #3) — Critical folds
 * into High for ordering (same rank). Recommendation is a new tier
 * below Low. Legacy 'Critical' rank retained so pre-migration data
 * still sorts to the top. */
const _SEV_RANK: Record<string, number> = {
  'Critical': 0,
  'High': 0,
  'Medium': 1,
  'Low': 2,
  'Recommendation': 3,
  'Pass': 4,
};

function _isSpa4SourcedIssue(issue: SpaIssueRow): boolean {
  const attrKey = (issue.attribute_key ?? '').toString().toLowerCase();
  const flagId = (issue.flag_id ?? '').toString().toUpperCase();
  return (
    attrKey.startsWith('clause_') || flagId.startsWith('SPA-CLAUSE-')
  );
}

/* Sprint 5 client feedback 2026-07-08 — synthesized issue row for a
   §4 clause the BE never emitted an issue for (e.g. LLM stored the
   clause as "Conservative" — no severity rule fires, so no BE issue
   row exists, but Conservative maps to Medium via computeSpa4Status
   and MUST surface in §5). Keeps the description short and honest:
   "'{display}' — {aiLabel}: review clause status."
   action_required / responsible use generic legal-review defaults so
   analysts don't see empty cells. */
function _synthesizeSpa4Issue(
  clause: SpaClauseRow,
  aiAnalysisLabel: string,
  status: 'Critical' | 'High' | 'Medium',
): SpaIssueRow {
  /* Iter9 #3 — status still typed as 'Critical' | 'High' | 'Medium'
   * because that is what computeSpa4Status returns; Critical is
   * kept as a legacy value only. Runtime display folds it to High. */
  const display = clause.display_name || clause.attribute_key || 'Clause';
  const attrUpper = String(clause.attribute_key ?? '').toUpperCase();
  return {
    flag_id: attrUpper ? `SPA-CLAUSE-${attrUpper}` : 'SPA-CLAUSE-UNKNOWN',
    severity: status,
    attribute_key: clause.attribute_key ?? null,
    description: `'${display}' — ${aiAnalysisLabel}: review clause status.`,
    source_page: clause.source_page ?? null,
    responsible: 'Legal',
    action_required:
      'Review clause terms and confirm alignment with investor protection standards.',
  };
}


function _transformSpa4Issues(
  issuesList: SpaIssueRow[],
  clausesList: SpaClauseRow[],
): SpaIssueRow[] {
  /* Sprint 5 client feedback 2026-07-08 — rewrite. Prior version
     walked the BE-emitted issuesList and dropped §4 rows whose
     computed Status = Pass. That left a hole: clauses whose stored
     Assessment is "Conservative" (or any category NOT covered by an
     SPA.yaml severity_rule) never generated a BE issue row, so
     even when computeSpa4Status maps them to Medium, they were
     silently missing from §5. Example (EH2 §4.5 ROFR on Transfer):
     Conservative → Medium in §4 but absent from §5.

     Fix: derive the §4-sourced §5 group DIRECTLY from clausesList,
     not from BE-emitted issues. For every clause row:
       * compute status via computeSpa4Status
       * if Pass → skip
       * else → emit a §5 issue at severity = status
     If a BE-emitted issue exists for the same attribute_key, we
     prefer its (description, action_required, responsible, flag_id)
     so the LLM-generated action text is preserved; we only
     override severity. If no BE issue exists, synthesize a
     minimal one via _synthesizeSpa4Issue so analysts still see a
     row instead of a gap.

     Non-§4 issues from rawIssuesList pass through unchanged
     (RG-1). §4-sourced issues from rawIssuesList are DISCARDED
     in favour of the clause-driven regeneration. */

  /* Build lookup from attribute_key → BE-emitted §4-sourced issue,
     used to preserve description / action_required / responsible /
     flag_id when the BE did emit one. */
  const beIssueByAttrKey = new Map<string, SpaIssueRow>();
  for (const issue of issuesList) {
    if (!_isSpa4SourcedIssue(issue)) continue;
    const k = String(issue.attribute_key ?? '');
    if (k && !beIssueByAttrKey.has(k)) {
      /* First BE issue per attribute_key wins — the SPA.yaml rule
         sequence puts the most severe rule (e.g. NOT_PRESENT) first,
         so first-wins matches BE deduplication semantics. */
      beIssueByAttrKey.set(k, issue);
    }
  }

  const spa4Group: SpaIssueRow[] = [];
  for (const clause of clausesList) {
    const attrKey = String(clause.attribute_key ?? '');
    if (!attrKey) continue;
    const { status, aiAnalysisLabel } = computeSpa4Status(clause);
    if (status === 'Pass') {
      // Pass rows are not issues — exclude from §5 entirely.
      continue;
    }
    const beIssue = beIssueByAttrKey.get(attrKey);
    if (beIssue) {
      /* BE-emitted issue exists — preserve its rich text but
         override severity with the computed status so §5 and §4
         agree on the tier. */
      spa4Group.push({ ...beIssue, severity: status });
    } else {
      /* No BE issue for this clause (BE severity rules didn't
         cover its stored category) — synthesize a minimal row. */
      spa4Group.push(_synthesizeSpa4Issue(clause, aiAnalysisLabel, status));
    }
  }

  /* Non-§4 issues pass through unchanged. */
  const nonSpa4Group: SpaIssueRow[] = issuesList.filter(
    (issue) => !_isSpa4SourcedIssue(issue),
  );

  /* Order §4-sourced group by:
       1. Severity: Critical → High → Medium (via _SEV_RANK)
       2. attribute_key ascending — this preserves the §4
          sub-card ordering because SPA.yaml sequences the
          clause_* attributes in sub-card order.
       3. Stable within tie (Array.sort is stable in modern JS) */
  spa4Group.sort((a, b) => {
    const sevA = _SEV_RANK[String(a.severity ?? 'Medium')] ?? 4;
    const sevB = _SEV_RANK[String(b.severity ?? 'Medium')] ?? 4;
    if (sevA !== sevB) return sevA - sevB;
    return (a.attribute_key ?? '').localeCompare(b.attribute_key ?? '');
  });

  /* §4-sourced group first (highest severity clauses lead the
     table), then non-§4 issues in their original relative order.
     This preserves the existing §5 behaviour where the most
     severe issues appear at the top of the table. */
  return [...spa4Group, ...nonSpa4Group];
}


// Public adapter — SPA §5 Issue list for cross-report consumers
// ─────────────────────────────────────────────────────────────────────────────

/* Sprint 5 client feedback 2026-07-09 + Iter6b (2026-07-11) —
   cross-report consistency ticket. Risk §9 / §11 / §12 / §15 / §16
   (and the PDF equivalents) must render the SAME Legal-category rows
   as SPA §4 Issue Identification & Classification (renumbered from
   §5 in Iter3 SPA #5). This adapter is the ONE reader that both the
   SPA renderer and the Risk consumers call, so a single source of
   truth drives every downstream surface — no LLM re-narration, no
   collapsing, no severity re-classification.

   Callers pass the SPA report payload's `attribute_groups` slice
   (whichever shape they have — the SPA page has it directly, the Risk
   page reads `spa.report.attribute_groups`). Returns the exact rows
   the SPA §4 IssuesTable renders, in the same order.

   Iter6b — renamed from `getSpaSection5Issues` to
   `getSpaIssueTableRows`. The legacy name is exported as an alias so
   external callers keep working without a coordinated migration. */
export function getSpaIssueTableRows(
  attributeGroups: Record<string, unknown> | null | undefined,
): SpaIssueRow[] {
  if (!attributeGroups || typeof attributeGroups !== 'object') return [];
  const groups = attributeGroups as {
    investor_protection?: SpaClauseRow[];
    issues?: SpaIssueRow[];
  };
  const clausesList: SpaClauseRow[] = Array.isArray(groups.investor_protection)
    ? groups.investor_protection
    : [];
  const rawIssuesList: SpaIssueRow[] = Array.isArray(groups.issues)
    ? groups.issues
    : [];
  return _transformSpa4Issues(rawIssuesList, clausesList);
}

/** @deprecated Iter6b — use getSpaIssueTableRows. The SPA issue list
 *  now renders under §4 (was §5 before Iter3 SPA #5). */
export const getSpaSection5Issues = getSpaIssueTableRows;


/* Companion adapter — expose Investor Protection clause rows + Universal
   /Jurisdiction rows for §12 Control Strengths, which needs the "Pass"
   rows from SPA §4 and §10 (the Universal SPA Structure sub-table). */
export function getSpaPassClauses(
  attributeGroups: Record<string, unknown> | null | undefined,
): SpaClauseRow[] {
  if (!attributeGroups || typeof attributeGroups !== 'object') return [];
  const groups = attributeGroups as {
    investor_protection?: SpaClauseRow[];
    mandatory_sections?: unknown[];
  };
  const clauses: SpaClauseRow[] = Array.isArray(groups.investor_protection)
    ? groups.investor_protection
    : [];
  return clauses.filter((c) => computeSpa4Status(c).status === 'Pass');
}


// ─────────────────────────────────────────────────────────────────────────────
// Public composer
// ─────────────────────────────────────────────────────────────────────────────

export function buildSpaSections(
  analysis: AnalysisResponse,
  status: AssessmentStatusResponse,
): SpaReportSection[] {
  const groups = analysis.attribute_groups ?? {};
  const sectionsList: SpaMandatorySectionRow[] = (groups as { mandatory_sections?: SpaMandatorySectionRow[] }).mandatory_sections ?? [];
  const clausesList: SpaClauseRow[] = (groups as { investor_protection?: SpaClauseRow[] }).investor_protection ?? [];
  const rawIssuesList: SpaIssueRow[] = (groups as { issues?: SpaIssueRow[] }).issues ?? [];
  /* FE Ticket 2026-07-08 — §5 §4-sourced issues re-derive severity
     from computeSpa4Status on the source clause row. Pass rows drop.
     Non-§4 issues untouched (RG-1). */
  const issuesList: SpaIssueRow[] = _transformSpa4Issues(rawIssuesList, clausesList);
  const signoffSnapshot: SpaSignoffSnapshot | null = analysis.signoff ?? null;
  /* Sprint 5 client feedback 2026-07-11 (Iter4 S10) — build the
     attribute_key → display_name lookup once so §4 IssuesTable can
     humanise each finding's Clause / Section column from the
     BE-curated display_name instead of falling to the deterministic
     splitter. */
  const clauseNameByKey = new Map<string, string>();
  for (const c of clausesList) {
    if (c.attribute_key && c.display_name) {
      clauseNameByKey.set(c.attribute_key, c.display_name);
    }
  }
  for (const s of sectionsList) {
    if (s.attribute_key && s.display_name) {
      clauseNameByKey.set(s.attribute_key, s.display_name);
    }
  }

  /* Sprint 5 client feedback 2026-07-11 (Iter3 SPA #5, #22) — two
     composer-level changes:
       * Document Overview moved from §2 to the FINAL numbered section
         so analysts read findings before document metadata.
         Renumbered §3..§6 → §2..§5 to close the gap. Document
         Overview becomes §6 (was §2).
       * Audit Trail section (§7) DROPPED — same content is already
         emitted in the downloaded DOCX/PDF report and duplicating it
         in the UI reads as noise. */
  return [
    {
      id: 'section-01',
      num: '01',
      title: 'Executive Summary',
      body: (
        <ExecutiveSummary
          analysis={analysis}
          sections={sectionsList}
          clauses={clausesList}
          issues={issuesList}
          signoff={signoffSnapshot}
        />
      ),
    },
    {
      id: 'section-02',
      num: '02',
      title: 'Mandatory Section Detection',
      body: <MandatorySectionDetectionTable sections={sectionsList} />,
    },
    {
      id: 'section-03',
      num: '03',
      title: 'Investor Protection Clauses',
      body: <InvestorProtectionClauses clauses={clausesList} />,
    },
    {
      id: 'section-04',
      num: '04',
      title: 'Issue Identification & Classification',
      body: <IssuesTable issues={issuesList} clauseNameByKey={clauseNameByKey} />,
    },
    {
      id: 'section-05',
      num: '05',
      title: 'Legal Findings Summary',
      body: (
        <LegalFindingsSummary
          signoff={signoffSnapshot}
          overallStatus={analysis.overall_status}
          synth={analysis.spa_legal_findings_synth ?? null}
        />
      ),
    },
    {
      id: 'section-06',
      num: '06',
      title: 'Document Overview',
      body: <DocumentOverview status={status} />,
    },
  ];
}
